import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { GoogleMap, useLoadScript } from "@react-google-maps/api"
import {
  defaultLat,
  defaultLng,
  googleAPIKeys,
  GOOGLE_MAPS_LIBRARIES,
} from "common/utils"
import { toLatLngLiteral, toLatLngPaths } from "common/geoBoundaryUtils"
import {
  createZoneMapLabelOverlayFromPaths,
  removeZoneMapLabelOverlays,
} from "common/zoneMapLabelOverlay"
import { useTranslation } from "react-i18next"

const mapContainerStyle = { width: "100%", height: "500px" }

const ZONE_COLORS = [
  { stroke: "#1976d2", fill: "#1976d2", label: "#1565c0" },
  { stroke: "#388e3c", fill: "#388e3c", label: "#2e7d32" },
  { stroke: "#f57c00", fill: "#f57c00", label: "#ef6c00" },
  { stroke: "#7b1fa2", fill: "#7b1fa2", label: "#6a1b9a" },
  { stroke: "#c62828", fill: "#c62828", label: "#b71c1c" },
  { stroke: "#00838f", fill: "#00838f", label: "#006064" },
]

const CityZonesMap = ({ zones = [] }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const mapRef = useRef(null)
  const polygonsRef = useRef([])
  const labelOverlaysRef = useRef([])
  const [mapReady, setMapReady] = useState(false)

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleAPIKeys,
    libraries: GOOGLE_MAPS_LIBRARIES,
  })

  const zonesWithBoundaries = useMemo(
    () =>
      (zones || []).filter(zone => zone?.boundary?.coordinates?.[0]?.length > 0),
    [zones]
  )

  const getZoneLabel = useCallback(
    zone =>
      language === "sa"
        ? zone?.nameAr || zone?.name
        : zone?.name || zone?.nameAr,
    [language]
  )

  const clearMapOverlays = useCallback(() => {
    polygonsRef.current.forEach(polygon => polygon.setMap(null))
    polygonsRef.current = []
    removeZoneMapLabelOverlays(labelOverlaysRef.current)
    labelOverlaysRef.current = []
  }, [])

  const drawZones = useCallback(
    map => {
      if (!map || !window.google?.maps) return

      clearMapOverlays()

      const bounds = new window.google.maps.LatLngBounds()
      let hasBounds = false

      zonesWithBoundaries.forEach((zone, index) => {
        const paths = toLatLngPaths(zone.boundary.coordinates[0])
        if (paths.length < 3) return

        const colors = ZONE_COLORS[index % ZONE_COLORS.length]
        const polygon = new window.google.maps.Polygon({
          paths,
          editable: false,
          draggable: false,
          clickable: false,
          map,
          strokeColor: colors.stroke,
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: colors.fill,
          fillOpacity: 0.2,
        })
        polygonsRef.current.push(polygon)

        const labelOverlay = createZoneMapLabelOverlayFromPaths(
          map,
          paths,
          getZoneLabel(zone),
          colors.stroke
        )
        if (labelOverlay) {
          labelOverlaysRef.current.push(labelOverlay)
        }

        paths.forEach(point => {
          const literal = toLatLngLiteral(point)
          if (literal) {
            bounds.extend(literal)
            hasBounds = true
          }
        })
      })

      if (hasBounds) {
        map.fitBounds(bounds)
      }
    },
    [clearMapOverlays, getZoneLabel, zonesWithBoundaries]
  )

  useEffect(() => {
    if (!isLoaded || !mapReady || !mapRef.current) {
      return
    }

    drawZones(mapRef.current)

    return clearMapOverlays
  }, [isLoaded, mapReady, drawZones, clearMapOverlays])

  if (!zones?.length) {
    return (
      <p className="text-muted mb-0 text-center my-4">
        {t("No zones found for this city")}
      </p>
    )
  }

  if (!zonesWithBoundaries.length) {
    return (
      <p className="text-muted mb-0 text-center my-4">
        {t("No zone boundaries defined")}
      </p>
    )
  }

  return (
    <div style={mapContainerStyle}>
      {!isLoaded ? (
        <div className="d-flex align-items-center justify-content-center h-100">
          {t("Loading")}...
        </div>
      ) : (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={{ lat: defaultLat, lng: defaultLng }}
          zoom={10}
          onLoad={map => {
            mapRef.current = map
            setMapReady(true)
          }}
          options={{
            draggable: true,
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: true,
          }}
        />
      )}
    </div>
  )
}

export default CityZonesMap
